import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, ProjectCard, EmptyState, Skeleton, ConfirmModal } from '@rb/ui'
import { useFetch } from '../hooks/useFetch'
import { apiFetch } from '../utils/api'
import styles from './Dashboard.module.css'

interface Project {
  id: string
  name: string
  status: string
  created_at: string
  updated_at: string
  file_count: number
}

export default function Dashboard() {
  const { data, loading, error, reload } = useFetch<Project[]>('/projects')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const navigate = useNavigate()

  async function handleDelete() {
    if (!deleteId) return
    setDeleting(true)
    try {
      await apiFetch(`/projects/${deleteId}`, { method: 'DELETE' })
      setDeleteId(null)
      reload()
    } catch {
      // keep modal open so user can retry
    } finally {
      setDeleting(false)
    }
  }

  const projects = data ?? []

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Analysen</h1>
        <Button variant="primary" onClick={() => navigate('/projects/new')}>
          + Neue Analyse
        </Button>
      </header>

      {loading && (
        <div className={styles.list}>
          <Skeleton className={styles.skeletonCard} />
          <Skeleton className={styles.skeletonCard} />
          <Skeleton className={styles.skeletonCard} />
        </div>
      )}

      {!loading && error && (
        <div className={styles.errorState}>
          <p>Fehler beim Laden</p>
          <Button variant="secondary" onClick={reload}>Erneut versuchen</Button>
        </div>
      )}

      {!loading && !error && projects.length === 0 && (
        <EmptyState
          heading="Noch keine Projekte"
          description="Laden Sie Ihren ersten Lastgang hoch und starten Sie die Analyse."
          ctaLabel="Ersten Lastgang hochladen"
          ctaTo="/projects/new"
        />
      )}

      {!loading && !error && projects.length > 0 && (
        <div className={styles.list}>
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => navigate(`/projects/${project.id}`)}
              onDelete={() => setDeleteId(project.id)}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Projekt löschen?"
        body='Diese Aktion kann nicht rückgängig gemacht werden. Das Projekt und alle hochgeladenen Dateien werden endgültig gelöscht.'
        confirmLabel="Endgültig löschen"
        cancelLabel="Abbrechen"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        loading={deleting}
      />
    </div>
  )
}
